import {List, update} from 'immutable';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {
  addAgentNumber,
  AgencyAssociation,
  changeTaxIdentificationNumber,
  EmptyAgencyAssociation,
  loadTaxIdentificationNumber,
  removeAgentNumber,
  ValidAgencyAssociation,
  changeIsAgencyAdministrator,
} from 'Users/models/User/Agent/AgencyAssociation';
import {AgentNumber} from 'Users/models/User/Agent/AgentNumber';
import {
  TaxIdentificationNumberWithNoAgentNumbers,
  ValidTaxIdentificationNumber,
} from 'Users/models/User/Agent/TaxIdentificationNumber';
import {AgentRole} from 'Users/models/User/AgentUserType';

export class UnvalidatedAgentUserType {
  public readonly key = 'UnvalidatedAgentUserType';
  public readonly agentRole = AgentRole.Agent;

  constructor(public readonly agencyAssociations: List<AgencyAssociation>) {}
}

export class InvalidAgentUserType {
  public readonly key = 'InvalidAgentUserType';
  public readonly agentRole = AgentRole.Agent;

  constructor(public readonly agencyAssociations: List<AgencyAssociation>) {}
}

const isEveryAgencyAssociationValid = (
  agencyAssociation: List<AgencyAssociation>
): agencyAssociation is List<ValidAgencyAssociation> =>
  agencyAssociation.every(aa => aa instanceof ValidAgencyAssociation);

export class ValidAgentUserType {
  public static create(agencyAssociations: List<AgencyAssociation>) {
    const hasDuplicateTaxId = agencyAssociations.map(aa => aa.taxId.value).toSet().size !== agencyAssociations.size;

    return agencyAssociations.count() > 0 && isEveryAgencyAssociationValid(agencyAssociations) && !hasDuplicateTaxId
      ? new ValidAgentUserType(agencyAssociations)
      : new InvalidAgentUserType(agencyAssociations);
  }

  public readonly key = 'ValidAgentUserType';
  public readonly agentRole = AgentRole.Agent;

  private constructor(public readonly agencyAssociations: List<ValidAgencyAssociation>) {}
}

export const addAgencyAssociation = ({agencyAssociations}: AgentUserType) => {
  const associations: List<AgencyAssociation> = agencyAssociations;

  return ValidAgentUserType.create(
    associations
      .push(EmptyAgencyAssociation)
      .map(({taxId, isAgencyAdministrator, agentNumbers}) =>
        ValidAgencyAssociation.create(taxId, isAgencyAdministrator, agentNumbers)
      )
  );
};

export const removeAgencyAssociation = (index: number) => ({agencyAssociations}: AgentUserType) => {
  const associations: List<AgencyAssociation> = agencyAssociations;

  return ValidAgentUserType.create(
    associations
      .remove(index)
      .map(({taxId, isAgencyAdministrator, agentNumbers}) =>
        ValidAgencyAssociation.create(taxId, isAgencyAdministrator, agentNumbers)
      )
  );
};

export const loadTaxIdNumber = (
  index: number,
  taxId: ValidTaxIdentificationNumber | TaxIdentificationNumberWithNoAgentNumbers
) => ({agencyAssociations}: AgentUserType) =>
  ValidAgentUserType.create(update(agencyAssociations as any, index, loadTaxIdentificationNumber(taxId)));

export const changeAgencyAssociationTaxId = (index: number, taxId: string) => ({agencyAssociations}: AgentUserType) =>
  ValidAgentUserType.create(update(agencyAssociations as any, index, changeTaxIdentificationNumber(taxId)));

export const changeAgencyAssociationIsAdministratorAccess = (index: number, isAdministrator: boolean) => ({
  agencyAssociations,
}: AgentUserType) =>
  ValidAgentUserType.create(update(agencyAssociations as any, index, changeIsAgencyAdministrator(isAdministrator)));

export const addAgentNumberToAgencyAssociation = (index: number, agentNumber: AgentNumber) => ({
  agencyAssociations,
}: AgentUserType) => ValidAgentUserType.create(update(agencyAssociations as any, index, addAgentNumber(agentNumber)));

export const removeAgentNumberFromAgencyAssociation = (index: number, agentNumber: AgentNumber) => ({
  agencyAssociations,
}: AgentUserType) =>
  ValidAgentUserType.create(update(agencyAssociations as any, index, removeAgentNumber(agentNumber)));

export type AgentUserType = ValidAgentUserType | InvalidAgentUserType | UnvalidatedAgentUserType;

export type AgentUserTypeOptions<TReturn> = ToMatchOptions<AgentUserType, 'key', TReturn>;

export const matchAgentUserType = <TReturn>(
  agentUserType: AgentUserType,
  options: AgentUserTypeOptions<TReturn>
): TReturn => {
  switch (agentUserType.key) {
    case 'InvalidAgentUserType':
      return options.InvalidAgentUserType(agentUserType);
    case 'UnvalidatedAgentUserType':
      return options.UnvalidatedAgentUserType(agentUserType);
    case 'ValidAgentUserType':
      return options.ValidAgentUserType(agentUserType);
  }
};

export const EmptyAgentUserType = new UnvalidatedAgentUserType(List([EmptyAgencyAssociation]));
